import React, { useEffect, useMemo, useRef, useState } from "react";
import CarPositions from "./CarPositions.svg";
import CarSpeed from "./CarSpeed.svg";

// Union type for label class names (nodes with id="label-xxx")
export type CarLabelItem = "label-rrs" | "label-rls" | "label-frs";

interface CarDiagramProps {
  onItemSelect: (item: CarLabelItem | null) => void;
  svgType?: "positions" | "speed";
}

export const CarDiagram: React.FC<CarDiagramProps> = ({ onItemSelect, svgType = "positions" }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  // Track when SVG content is ready
  const [svgReady, setSvgReady] = useState<boolean>(false);

  // Track which items are currently hovered (showing -active version on hover)
  const [hoveredItems, setHoveredItems] = useState<Set<CarLabelItem>>(new Set());

  useEffect(() => {
    setMounted(true);
  }, []);

  // Effect to detect when SVG content is available
  useEffect(() => {
    if (!mounted || !divRef.current) {
      setSvgReady(false);
      return;
    }

    // Use a timeout to ensure SVG is fully rendered
    const timeoutId = setTimeout(() => {
      setSvgReady(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [mounted]);

  // memoize item handles - recalculate when SVG is ready
  const itemHandles = useMemo<Partial<Record<CarLabelItem, Element>>>(() => {
    if (!svgReady || !divRef.current) {
      return {};
    }

    const handles = {
      "label-rrs": divRef.current.querySelector(".label-rrs") ?? undefined,
      "label-rls": divRef.current.querySelector(".label-rls") ?? undefined,
      "label-frs": divRef.current.querySelector(".label-frs") ?? undefined,
    };

    return handles;
  }, [svgReady]);

  // Effect to add 'selected' class to hovered labels
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    // Toggle 'selected' class on labels based on hover state
    Object.entries(itemHandles).forEach(([label, handle]) => {
      const labelItem = label as CarLabelItem;
      const isHovered = hoveredItems.has(labelItem);

      if (handle) {
        if (isHovered) {
          handle.classList.add("selected");
        } else {
          handle.classList.remove("selected");
        }
      }
    });
  }, [svgReady, hoveredItems, itemHandles]);

  // setup hover and click listeners
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    const clickListeners: Record<string, (event: Event) => void> = {};
    const mouseEnterListeners: Record<string, (event: Event) => void> = {};
    const mouseLeaveListeners: Record<string, (event: Event) => void> = {};

    // Add hover and click listeners to individual labels
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (!handle) {
        // eslint-disable-next-line no-console
        return;
      }

      const labelItem = id as CarLabelItem;

      // Click listener - triggers onItemSelect
      clickListeners[id] = (event: Event) => {
        // Prevent event from bubbling to the container
        event.stopPropagation();
        onItemSelect(labelItem);
      };

      // Mouse enter listener - shows active state
      mouseEnterListeners[id] = () => {
        setHoveredItems(new Set([labelItem]));
      };

      // Mouse leave listener - hides active state
      mouseLeaveListeners[id] = () => {
        setHoveredItems(new Set());
      };

      handle.addEventListener("click", clickListeners[id]);
      handle.addEventListener("mouseenter", mouseEnterListeners[id]);
      handle.addEventListener("mouseleave", mouseLeaveListeners[id]);
    });

    // Add click listener to the container for click-outside detection
    const containerClickListener = (event: Event): void => {
      // Check if the click target is one of the label elements
      const target = event.target as Element;
      const isLabelClick = Object.values(itemHandles).some(
        (handle) => handle && (handle === target || handle.contains(target))
      );

      if (!isLabelClick) {
        onItemSelect(null);
      }
    };

    const containerElement = divRef.current;
    containerElement.addEventListener("click", containerClickListener);

    return () => {
      // Remove all listeners
      Object.entries(itemHandles).forEach(([id, handle]) => {
        if (handle) {
          if (clickListeners[id]) {
            handle.removeEventListener("click", clickListeners[id]);
          }
          if (mouseEnterListeners[id]) {
            handle.removeEventListener("mouseenter", mouseEnterListeners[id]);
          }
          if (mouseLeaveListeners[id]) {
            handle.removeEventListener("mouseleave", mouseLeaveListeners[id]);
          }
        }
      });

      // Remove container listener
      if (containerElement) {
        containerElement.removeEventListener("click", containerClickListener);
      }
    };
  }, [svgReady, itemHandles, onItemSelect]);

  const SvgComponent = svgType === "speed" ? CarSpeed : CarPositions;

  return (
    <div ref={divRef}>
      <SvgComponent />
      {/* Inline styles for selected state */}
      <style>
        {`
        .selected rect {
          stroke: #6E32F0 !important;
          stroke-width: 2 !important;
        }
      `}
      </style>
    </div>
  );
};
